import { Card, CardBody, Text, useToast } from "@chakra-ui/react";
import type { DateStatus } from "@models/Order";
import { categorizeDate, formatSwedishDate, getFormatDate, getFormatTime } from "@utils/dateUtils";
import { BreakTimeList } from "@constants/createOrder";
import moment from "moment";
import { appTheme } from "@styles/Theme";

interface DateItemProps {
	onEditWeekSchedule: (type: "WEEK" | "DAY", clickedDate?: string) => void;
	start: string;
	end: string;
	breakTime?: number;
	tempStatus?: "BOOKED_WITH_CONSULTANT" | "BOOKED_WITHOUT_CONSULTANT" | "TEMPORARY_ADDED" | "DEFAULT";
}

const DateItem = ({ start, end, breakTime, onEditWeekSchedule, tempStatus = "DEFAULT" }: DateItemProps) => {
	const isSelectedDate = getFormatTime(start) !== "00:00" && getFormatTime(end) !== "00:00";
	const dateStatus = isSelectedDate ? "SelectedDate" as DateStatus : start && categorizeDate(start);
	const toast = useToast();

	const getDateTextColor = () => {
		if ((tempStatus = "DEFAULT")) {
			switch (dateStatus) {
				case "SelectedDate":
					return "#FFFFFF";
				case "DisabledDate":
					return "#C4C4C4";
				case "WeekEndDate":
					return "#C44747";
				default:
					return "#333333";
			}
		} else {
			return "#FFFFFF";
		}
	};

	const getBreakTime = () => {
		const breakTimeHours = BreakTimeList.find((item) => Number(item.key) === breakTime)?.value;
		if (breakTimeHours) return breakTimeHours;
		return breakTime && breakTime * 60;
	};

	const getBackgroundColor = () => {
		if (tempStatus === "DEFAULT") {
			return isSelectedDate ? appTheme.colors.accent : "#FFFFFF";
		}
		if (tempStatus === "BOOKED_WITHOUT_CONSULTANT") {
			return appTheme.colors.accent;
		}
		if (tempStatus === "BOOKED_WITH_CONSULTANT") {
			return "#9F9F9F";
		}
		if (tempStatus === "TEMPORARY_ADDED") {
			return "#FFA500";
		}
	};

	const disableOnClick = (): boolean => {
		if (tempStatus === "DEFAULT") return dateStatus === "DisabledDate";
		return tempStatus === "BOOKED_WITH_CONSULTANT";
	};

	const handleDateClick = () => {
		disableOnClick()
			? undefined
			: moment().isAfter(start, "day")
				? toast({
						title: "Can't update past dates for the shift.",
						status: "warning",
						duration: 9000,
						isClosable: true,
						variant: "subtle",
					})
				: onEditWeekSchedule(isSelectedDate ? "WEEK" : "DAY", isSelectedDate ? undefined : getFormatDate(start));
	};

	return (
		<Card
			variant="elevated"
			width={120}
			height={90}
			_hover={{ opacity: 0.5 }}
			bg={getBackgroundColor()}
			cursor={dateStatus !== "DisabledDate" ? "pointer" : "default"}
			onClick={handleDateClick}
		>
			<CardBody p={2}>
				<Text color={getDateTextColor()} fontSize={14} fontWeight={500} m={0}>
					{formatSwedishDate(start, "short")}
				</Text>
				{isSelectedDate && (
					<>
						<Text color={"#FFFFFF"} fontSize={12} fontWeight={400} mt={2} mb={0}>
							{`${getFormatTime(start)} - ${getFormatTime(end)}`}
						</Text>
						{breakTime !== undefined && breakTime !== null ? (
							<Text color={"#FFFFFF"} fontSize={10} fontWeight={400} mb={1}>{`Rast: ${getBreakTime()} min`}</Text>
						) : null}
					</>
				)}
			</CardBody>
		</Card>
	);
};

export default DateItem;
